import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaMarkup from '@/components/feature/SchemaMarkup';
import { categories } from '@/mocks/categories';
import { articles, getArticlesByCategory } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const [activeSlug, setActiveSlug] = useState<string>(slug || '');

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryArticles = activeSlug ? getArticlesByCategory(activeSlug) : [];
  const allCategories = categories;

  const handleCategoryClick = (catSlug: string) => {
    setActiveSlug(catSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageTitle = currentCategory
    ? `${currentCategory.name} - 禁漫天堂内容分类`
    : `内容分类 - ${siteInfo.name}`;
  const pageDesc = currentCategory
    ? currentCategory.description
    : '浏览禁漫天堂全部内容分类，涵盖日漫、韩漫、国漫、欧美、3D漫画、同人作品等多种类型，快速找到你感兴趣的漫画内容。';
  const canonicalUrl = currentCategory
    ? `${siteInfo.url}/category/${activeSlug}`
    : `${siteInfo.url}/category`;

  const breadcrumbItems = currentCategory
    ? [
        { name: '首页', path: '/' },
        { name: '内容分类', path: '/category' },
        { name: currentCategory.name },
      ]
    : [
        { name: '首页', path: '/' },
        { name: '内容分类' },
      ];

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: (currentCategory ? categoryArticles : allCategories).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': currentCategory ? 'Article' : 'Thing',
        name: currentCategory ? (item as typeof articles[0]).title : (item as typeof categories[0]).name,
        url: currentCategory
          ? `${siteInfo.url}/article/${(item as typeof articles[0]).slug}`
          : `${siteInfo.url}/category/${(item as typeof categories[0]).slug}`,
      },
    })),
  };

  return (
    <Layout>
      <SEOMeta
        title={pageTitle}
        description={pageDesc}
        keywords={currentCategory ? `${currentCategory.name},禁漫天堂,内容分类` : '禁漫天堂内容分类,漫画类型,日漫,韩漫,国漫'}
        canonicalUrl={canonicalUrl}
      />
      <SchemaMarkup schema={itemListSchema} />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb items={breadcrumbItems} />

        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)] mb-2">
          {currentCategory ? currentCategory.name : '内容分类'}
        </h1>
        {currentCategory && (
          <p className="text-sm text-foreground-600 mb-8">{currentCategory.description}</p>
        )}

        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => handleCategoryClick('')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
              !activeSlug
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            全部
          </button>
          {allCategories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => handleCategoryClick(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </button>
          ))}
        </div>

        {!activeSlug ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {allCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className="text-left group p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 flex items-start gap-4 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-500 transition-colors duration-300">
                  <i className={`${cat.icon} text-xl text-primary-500 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-foreground-950 mb-1 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-2">
                    {cat.description}
                  </p>
                  <span className="text-xs text-foreground-400">
                    {cat.articleCount} 部作品
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div>
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 flex flex-col"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4 flex-1">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400 mt-auto">
                        <span>{article.date}</span>
                        <span>{article.readTime} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}